import EmptyState from 'components/EmptyState/EmptyState';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import 'features/Weather/assets/weatherDataDisplay.css';
import useLocation from 'hooks/useLocation';
import { Alert, Button, Container } from 'react-bootstrap';
import { useGetAirportDelayForecastQuery } from 'services/weather/airports/airports.api';
import AirportDelayAccordion from './components/AirportDelayAccordion/AirportDelayAccordion';
import './assets/airportDelays.scss';

/**
 * Loads airport forecasts for the selected location and displays an accordion for each airport.
 * Keeps the last available reports visible while refreshed data is being requested.
 */
const AirportDelays = () => {
  const { locations } = useLocation();
  const location = locations.effectiveLocation;
  const isLocationLoading = !location || location.isLoading;
  const { data, isLoading, isFetching, isError, refetch } = useGetAirportDelayForecastQuery(
    location,
    {
      skip: isLocationLoading,
    },
  );
  const reports = Array.isArray(data) ? data.filter(Boolean) : [];
  const hasReports = reports.length > 0;

  // Only replace the page with a loader when there are no reports to keep on screen.
  if (isLocationLoading || ((isLoading || isFetching) && !hasReports)) {
    return <WidgetLoadingAnimation />;
  }

  return (
    <Container
      fluid
      className="airport-delays weather-data-display d-flex flex-column h-100 gap-3 px-0"
    >
      <WidgetTimeUpdate route="getAirportDelayForecast" />
      <div className="weather-data-display-heading d-flex align-items-center justify-content-between gap-3 border-bottom pb-2">
        <h2 className="weather-data-display-title m-0 fw-bold lh-sm">Airport Delays</h2>
        {isFetching && hasReports && (
          <span className="small" role="status">
            Updating...
          </span>
        )}
      </div>

      {isError && (
        <Alert variant="danger" className="m-0 d-flex align-items-center flex-wrap gap-3">
          <p className="m-0 flex-grow-1">
            {hasReports
              ? 'Unable to update airport delays. Showing the last available results.'
              : 'Unable to load airport delays. Please try again.'}
          </p>
          <Button variant="primary" size="sm" onClick={refetch} disabled={isFetching}>
            Retry
          </Button>
        </Alert>
      )}

      {!isError && !hasReports && (
        <EmptyState
          title="Airport Delays"
          message="No airport delay forecasts are available for this location."
        />
      )}

      {hasReports && (
        <div className="airport-delays-scroll flex-grow-1 overflow-auto">
          <div className="d-flex flex-column gap-3 pb-3">
            {reports.map((report, index) => (
              <AirportDelayAccordion
                key={`${report.location ?? 'airport'}-${index}`}
                report={report}
              />
            ))}
          </div>
        </div>
      )}
    </Container>
  );
};

export default AirportDelays;
